import cloneDeep from 'clone-deep'
import { PaymentTermsConditions } from 'components/PaymentTermsConditions'
import { type InputType, MIN_CARD_DIGITS } from 'config'
import { useEffect, useState } from 'react'
import { toast } from 'react-toastify'
import { chargeUsio, createPayment, PaymentOrderType, StripeMethod } from 'services'
import { Button } from 'stories/components'
import { getItemsFromFullAddress, getPrice2decimal, InputConvert, InputValidate } from 'utils'
import { RenderInput } from 'utils/RenderInput'

const billingInputs = (): Record<string, InputType> => ({
  name: {
    inputType: 'text',
    type: 'text',
    title: 'Cardholder / Account Holder Name',
    required: true,
    span: 'full',
  },
  address: {
    inputType: 'map',
    title: 'Billing Address',
    required: true,
    span: 'full',
    allowForeign: true,
  },
})

const cardInputs = (): Record<string, InputType> => ({
  cardNumber: { inputType: 'text', type: 'text', title: 'Card Number', required: true, span: 'full' },
  expDate: { inputType: 'text', type: 'text', title: 'Expiration (MM/YY)', required: true },
  cvv: { inputType: 'text', type: 'text', title: 'CVV', required: true },
})

const achInputs = (): Record<string, InputType> => ({
  routingNumber: { inputType: 'text', type: 'routingNo', title: 'Routing Number', required: true },
  accountNumber: { inputType: 'text', type: 'bankAccountNo', title: 'Account Number', required: true },
  accountType: {
    inputType: 'select',
    title: 'Account Type',
    options: { checking: 'Checking', savings: 'Savings' },
    hasDefaultOption: false,
    required: true,
    value: 'checking',
  },
})

/**
 * USIO direct collector (Path B): renders our own inline card / ACH fields and posts the raw
 * details to the backend, which charges them through USIO's Payments API. No Checkout.js, no popup.
 * The field values are sent once to /payment/usio/charge and are never cached or logged here.
 */
export const UsioCheckout = ({
  orderType,
  refId,
  amount,
  method,
  data,
  lenderName,
  setLoading = () => {},
  onCompleted = () => {},
  onCardNumberChange = () => {},
}: {
  email: string
  orderType: PaymentOrderType
  refId: string
  amount: number
  method: StripeMethod
  data: Record<string, any>
  lenderName?: string
  setLoading?: Function
  onCompleted?: Function
  /**
   * Reports the keyed card number ('' until a full one is typed) to the parent, which needs it to
   * quote the convenience fee — the gateway prices debit and credit cards differently and looks the
   * rate up from the number.
   *
   * This used to hand up only the first six digits. That is not enough: USIO answers a six-digit
   * prefix with success and a 0.00 fee rather than an error, so the payer was shown no convenience
   * fee while the charge was priced on the full number. The number goes no further than the parent,
   * which sends it to our own backend for the quote — the same trip this component already makes to
   * charge it.
   */
  onCardNumberChange?: (cardNumber: string) => void
}) => {
  const isAch = method === StripeMethod.us_bank_account
  const [intentId, setIntentId] = useState('')
  const [message, setMessage] = useState<string | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isCompleted, setCompleted] = useState(false)
  const [isTermsAgreed, setIsTermsAgreed] = useState(false)
  const [inputs, setInputs] = useState<Record<string, InputType>>(() => ({
    ...(isAch ? achInputs() : cardInputs()),
    ...billingInputs(),
  }))

  // Swap the payment fields when the user toggles card vs ACH.
  useEffect(() => {
    setInputs({ ...(isAch ? achInputs() : cardInputs()), ...billingInputs() })
  }, [isAch])

  // Create (or reuse) the intent so the backend has a row to charge against.
  useEffect(() => {
    if (!method) return
    let cancelled = false
    setLoading(true)
    createPayment({ orderType, refId, method, amount, data })
      .then((res) => {
        if (cancelled) return
        if (!res?.intentId) {
          toast('Failed to initialize USIO payment', { type: 'error' })
          return
        }
        setIntentId(res.intentId)
      })
      .finally(() => setLoading(false))
    return () => {
      cancelled = true
    }
  }, [method, amount])

  const onChange = (key: string, value: string) => {
    const newInputs = cloneDeep(inputs)
    value = InputConvert(newInputs[key], value)
    newInputs[key].error = InputValidate({ ...newInputs[key], value })
    newInputs[key].value = value
    setInputs(newInputs)
    if (key === 'cardNumber') {
      const digits = String(value || '').replace(/\D/g, '')
      onCardNumberChange(digits.length >= MIN_CARD_DIGITS ? digits : '')
    }
  }

  const onSubmit = async (e: any) => {
    e.preventDefault()
    if (!intentId) {
      toast('Payment is not ready yet', { type: 'error' })
      return
    }
    if (!isTermsAgreed) {
      toast('Please agree to the payment terms', { type: 'error' })
      return
    }

    let hasError = false
    const newInputs = cloneDeep(inputs)
    const form: Record<string, any> = {}
    for (const key in inputs) {
      if (newInputs[key].value && (newInputs[key].value as any).trim) {
        newInputs[key].value = (newInputs[key].value as string).trim()
      }
      newInputs[key].error = InputValidate(newInputs[key])
      form[key] = newInputs[key].value
      if (newInputs[key].error) hasError = true
    }
    setInputs(newInputs)
    if (hasError) return

    setMessage(null)
    setIsSubmitting(true)
    setLoading(true)
    try {
      const nameParts = String(form.name || '').trim().split(/\s+/)
      const firstName = nameParts[0] || 'NA'
      const lastName = nameParts.slice(1).join(' ') || firstName
      const addr = (await getItemsFromFullAddress(form.address)) || ({} as any)

      const billing = {
        intentId,
        firstName,
        lastName,
        address1: addr.street_address1 || 'NA',
        address2: '',
        city: addr.city || 'NA',
        state: (addr.state || 'NA').slice(0, 2),
        zip: addr.postal_code || '00000',
      }

      const payload = isAch
        ? {
            ...billing,
            routingNumber: form.routingNumber,
            accountNumber: form.accountNumber,
            accountType: form.accountType,
          }
        : {
            ...billing,
            cardNumber: form.cardNumber,
            expDate: form.expDate,
            cvv: form.cvv,
          }

      // Every method switch re-saves the same pending intent, and the backend charges whichever
      // method that row holds, not the fields sent. Those saves can land out of order, so a payer who
      // went card -> ACH -> card could have their card sent to the ACH endpoint. Save it once more
      // with what is on screen now, right before charging.
      const current = await createPayment({ orderType, refId, method, amount, data })
      if (!current?.intentId) throw new Error('Payment is not ready yet. Please try again.')

      const result = await chargeUsio({ ...payload, intentId: current.intentId })

      if (result?.status === 'payment_failed') {
        setMessage(result.message || 'Payment was declined. Please check your details and try again.')
      } else {
        setCompleted(true)
        onCompleted(result?.intentId || intentId)
      }
    } catch (err: any) {
      setMessage(err?.message || 'Failed to process payment')
    } finally {
      setIsSubmitting(false)
      setLoading(false)
    }
  }

  if (!intentId) return null

  return (
    <form onSubmit={onSubmit} className="space-y-3">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {Object.keys(inputs).map((key) => (
          <RenderInput key={key} input={inputs[key]} Key={key} onChange={onChange} />
        ))}
      </div>
      <PaymentTermsConditions
        orderType={orderType}
        amount={amount}
        lenderName={lenderName}
        onChangeTerms={setIsTermsAgreed}
      />
      {message && <p className="text-red-600 text-sm">{message}</p>}
      <Button type="submit" full loading={isSubmitting} disabled={isCompleted}>
        {isCompleted ? 'Paid' : `Pay $${getPrice2decimal(amount)}`}
      </Button>
    </form>
  )
}

export default UsioCheckout
